import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import {
  Row,
  Col,
  Modal,
  Alert,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import useCurrentPage from '../../../../hooks/useCurrentPage';
import { TRACK_CLICK_RESERVE, TRACK_CLICK_MOVE_IN } from '../../../../../src/web/utils/dataLayer';
import UnitDescription from '../../../../components/stateful/UnitsTable/columns/Actions/actions/UnitDescription';
import Button from '../../../../../modules/react/components/stateless/Button';
import useYupValidationResolver from '../../hooks/useYupValidationResolver';
import RentalForm from '../RentalForm';
import AmountDueToday from '../../../../components/stateful/UnitsTable/columns/Actions/actions/AmountDueToday';
import AmountDueAtMoveIn from '../../../../components/stateful/UnitsTable/columns/Actions/actions/AmountDueAtMoveIn';
import styles from '../../../../components/stateful/UnitsTable/columns/Actions/Actions.scss';

const propTypes = {
  facility: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    settings: PropTypes.shape({
      reservationDisclaimer: PropTypes.string,
    }).isRequired,
    unitGroup: PropTypes.object.isRequired,
  }).isRequired,
  moveInText: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  reserveText: PropTypes.string.isRequired,
  submitReservation: PropTypes.func.isRequired,
  submitErrors: PropTypes.array,
  submitting: PropTypes.bool.isRequired,
  onPromoUpdate: PropTypes.func.isRequired,
  selectedPlan: PropTypes.object.isRequired,
  discountPlans: PropTypes.array.isRequired,
};

const defaultProps = {
  submitErrors: [],
};

const phoneRegExp = /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/;

const validationSchema = object().shape({
  email: string()
    .email('Email is invalid')
    .required('Email is required'),
  phoneNumber: string()
    .matches(phoneRegExp, 'Phone number is not valid')
    .required('Phone Number is required'),
});

const RentalModal = ({
  facility,
  moveInText,
  onClose,
  reserveText,
  submitting,
  submitReservation,
  submitErrors,
  onPromoUpdate,
  discountPlans,
  appliedPromos,
  selectedDiscountPlan,
  tier,
  ...rest
}) => {
  const {
    secondaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    buttonStyleClass,
    primaryColorClass,
    secondaryColorBorderClass,
    primaryColorBorderHoverClass,
    secondaryColorClass,
    primaryColorHoverClass,
    primaryColorBackgroundClass,
    secondaryColorHoverClass,
  } = useCurrentPage();

  let { price = 0, discountedPrice = 0 } = rest;

  if (tier && tier.price) {
    ({ price, discountedPrice } = tier);
  }

  const { settings: { hidePricingDisplayCall, enablePublicDescription,
    softwareProvider } } = facility;

  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    register,
    setValue,
    getValues,
  } = useForm({
    resolver,
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
    },
    mode: 'onBlur',
  });

  const setType = (type) => {
    if (!getValues('type')) {
      register('type');
    }

    setValue('type', type);
  };

  const renderErrorContents = () => (
    <ul>
      {submitErrors.map((err, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index}>{err.message}</div>
      ))}
    </ul>
  );

  const renderError = () => {
    if (submitErrors.length === 0) {
      return false;
    }
    return (
      <Alert variant="danger">
        {renderErrorContents()}
      </Alert>
    );
  };

  return (
    <Modal
      onHide={onClose}
      show
    >
      <form onSubmit={handleSubmit(submitReservation)}>
        <Modal.Header className={`${styles.modalHeader} ${styles.close}`} closeButton>
          <div className={`${styles.reserveTitle} ${primaryColorClass}`}>
            Get Started
          </div>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.blurb}>
            <p>
              You have selected to rent a storage unit at {facility.displayName}.
              If you have selected the wrong size, go back and make a new
              selection.
            </p>
            {facility.settings.reservationDisclaimer}
          </div>
          <div className={styles.subHeading}>
            Size / Features
          </div>
          <Row className={styles.descriptionWell}>
            <Col xs={12}>
              <UnitDescription
                styles={styles}
                {...facility.unitGroup}
              />
            </Col>
          </Row>

          <AmountDueToday reservationFee={0} />

          <AmountDueAtMoveIn
            selectedPlan={selectedDiscountPlan}
            price={price}
            discountedPrice={discountedPrice}
            discountPlans={discountPlans}
            onPromoUpdate={onPromoUpdate}
            primaryColorClass={primaryColorClass}
            primaryColorBackgroundClass={primaryColorBackgroundClass}
            secondaryColorHoverClass={secondaryColorHoverClass}
            secondaryColorBackgroundClass={secondaryColorBackgroundClass}
            primaryColorBackgroundHoverClass={primaryColorBackgroundHoverClass}
            hidePricingDisplayCall={hidePricingDisplayCall}
            enablePublicDescription={enablePublicDescription}
            softwareProvider={softwareProvider}
            appliedPromos={appliedPromos}
            buttonStyleClass={buttonStyleClass}
          />

          {renderError()}

          <RentalForm
            control={control}
            className={styles.form}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="submitReservation"
            data-event="submit-reservation"
            variant="primary"
            id="reserve"
            onClick={() => setType('reservation')}
            className={`
              ${secondaryColorBorderClass}
              ${primaryColorBorderHoverClass}
              ${secondaryColorClass}
              ${primaryColorHoverClass}
              ${buttonStyleClass}
            `}
            // Inlining the style to override the root defaults
            style={{
              backgroundColor: '#fff',
              border: '1px solid',
            }}
            disabled={submitting}
            {...TRACK_CLICK_RESERVE}
          >
            {submitting && getValues('type') === 'reservation' ? 'Submitting...' : reserveText}
          </Button>
          <Button
            type="submit"
            variant="primary"
            id="movein"
            onClick={() => setType('move-in')}
            data-event="move-in"
            className={`${secondaryColorBackgroundClass} ${primaryColorBackgroundHoverClass} ${buttonStyleClass}`}
            data-testid="actions-movein"
            disabled={submitting}
            {...TRACK_CLICK_MOVE_IN}
          >
            {submitting && getValues('type') === 'move-in' ? 'Submitting...' : moveInText}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

RentalModal.fragments = {
  facility: gql`
    fragment RentalModalFacility on Facility {
      displayName
      settings {
        reservationDisclaimer
        softwareProvider
        hidePricingDisplayCall
        enablePublicDescription
      }

    }
  `,
  unitGroup: gql`
    fragment RentalModalUnitGroup on UnitGroup {
      ...UnitDescriptionUnitGroup
    }
    ${UnitDescription.fragments.unitGroup}
  `,
};

RentalModal.propTypes = propTypes;
RentalModal.defaultProps = defaultProps;

export default RentalModal;
